// src/pages/BankManagement/BankManagement.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faTrophy,
  faBalanceScale
} from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

// Define the Bank Management options
const bankManagementOptions = [
  {
    title: 'Seller Settlement',
    icon: faExchangeAlt,
    path: '/bank/seller-settlement',
    modules: [7100],
    description: 'Manage seller payment settlements'
  },
  {
    title: 'Successful Fee',
    icon: faTrophy,
    path: '/bank/shop-transactions',
    modules: [7101],
    description: 'View successful transaction fees'
  },
  {
    title: 'Failed Fee',
    icon: faBalanceScale,
    path: '/bank/shop-failed-transactions',
    modules: [7102],
    description: 'Review failed transaction charges'
  }
];

const BankManagement = () => {
  const [userLevel, setUserLevel] = useState(null);
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchUserPrivileges = useCallback(async (userId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/privileges/modules/${userId}`, {
        timeout: 5000
      });
      
      if (response.status === 200 && response.data.modules) {
        setUserModules(response.data.modules);
      } else {
        setError('No modules found for user');
      }
    } catch (error) {
      setError('Failed to load user privileges');
      console.error('Error fetching user privileges:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setError('Please login to continue');
      setIsLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);

      if (decodedToken.level === 1) {
        // Super admin gets access to all modules
        setUserModules(bankManagementOptions.flatMap(option => option.modules));
        setIsLoading(false);
      } else {
        // For other users, fetch their specific module permissions
        fetchUserPrivileges(decodedToken.userId);
      }
    } catch (error) {
      setError('Invalid authentication token');
      setIsLoading(false);
    }
  }, [fetchUserPrivileges]);

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const hasAccess = useCallback((modules) => {
    return modules.some(moduleId => userModules.includes(moduleId));
  }, [userModules]);

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography color="error">{error}</Typography>
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = bankManagementOptions.some(option => hasAccess(option.modules));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access bank management features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Bank Management
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {bankManagementOptions.map((option) => (
          hasAccess(option.modules) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      style={{ color: 'orange', marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default BankManagement;